import { CommonModule } from "@angular/common";
import { Component, inject, Input, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { SERIE_REPOSITORY } from "@app/app.providers";
import { RequestProductGrowthComponent } from "@app/shared/components/request-product-growth/request-product-growth.component";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { AccountingPlanService } from "@app/shared/services/accounting-plan.service";
import { ChatNandaService } from "@app/shared/services/chat-nanda.service";
import { ContactService } from "@app/shared/services/contact.service";
import { DeviceService } from "@app/shared/services/device.service";
import { VisualizationEventsService } from "@app/shared/services/visualization-events.service";
import { VisualizationPostsComponent } from "@app/views/auth/report/components/visualization-posts/visualization-posts.component";
import { SourceConvert } from "@arch/_shared/utils";
import {
    CompanyEntity,
    PointEntity,
    ReportEntity,
    VisualizationEntity
} from "@arch/domain/entity";
import { VisualizationComponent } from "@shared/components/visualization/visualization.component";
import _ from "lodash";
import { ReportVisualizationExploreComponent } from "../report-visualization-explore/report-visualization-explore.component";
import { ReportVisualizationNoteComponent } from "../report-visualization-note/report-visualization-note.component";

@Component({
    selector: "app-report-visualization",
    imports: [
        CommonModule,
        SharedDsnModule,
        SharedMaterialModule,
        VisualizationComponent,
        VisualizationPostsComponent,
        ReportVisualizationExploreComponent,
        ReportVisualizationNoteComponent
    ],
    templateUrl: "./report-visualization.component.html",
    styleUrl: "./report-visualization.component.scss"
})
export class ReportVisualizationComponent implements OnInit {
    @Input({ required: true }) visualization: VisualizationEntity;
    @Input({ required: true }) company: CompanyEntity;
    @Input({ required: true }) report: ReportEntity;
    @Input({ required: true }) loading: boolean;
    @Input({ required: false }) previewMode: boolean;
    @Input({ required: false }) pdf: boolean;
    @Input({ required: false }) linkDigital: string = "";

    private readonly deviceService = inject(DeviceService);
    public readonly accountingPlanService = inject(AccountingPlanService);
    public readonly chatNandaService = inject(ChatNandaService);
    public readonly dialog = inject(MatDialog);
    private readonly visualizationEventsService = inject(
        VisualizationEventsService
    );
    public contactService = inject(ContactService);
    private readonly serieRepository = inject(SERIE_REPOSITORY);
    public isMobile = this.deviceService.getIsMobile();
    public points: PointEntity[] = [];
    public mainVisualization: VisualizationEntity;
    public contact = this.contactService.get();
    public isAccumulated: boolean = false;

    public readonly SourceConvert = SourceConvert;

    ngOnInit(): void {
        this.mainVisualization = this.visualization;
        this.visualization.setPDFMode(this.pdf);
        this.setIsAccumulated(this.visualization.props.series);
    }

    public onExpand(): void {
        this.visualizationEventsService.emitExpandEvent({
            visualizationId: this.visualization.props._id,
            componentType: "table-dre",
            data: {
                visualization: this.visualization,
                company: this.company,
                report: this.report
            }
        });
    }

    public hasValiData() {
        return (
            this.hasPointsData(this.points) &&
            this.hasNonZeroValuesData(this.points)
        );
    }

    public onPointsChange(points: PointEntity[]): void {
        this.points = points;
    }

    private hasNonZeroValuesData(points: PointEntity[]): boolean {
        if (!points || points.length === 0) {
            return false;
        }

        return points.some((point) => {
            const value = point.props.m?.v || point.props.m?.f || 0;
            return value !== 0 && value !== null && value !== undefined;
        });
    }

    private hasPointsData(points: PointEntity[]): boolean {
        return points && points.length > 0;
    }

    public onSelectVisualizationDrillDown(
        visualization: VisualizationEntity
    ): void {
        this.loading = true;
        const _visualization = _.cloneDeep(visualization);
        if (visualization.props._id != this.mainVisualization.props._id)
            _visualization.transformChartToDefaultColumn();
        this.visualization = VisualizationEntity.restore(_visualization.props);
        this.report = _.cloneDeep(this.report);
        setTimeout(() => {
            this.loading = false;
        }, 1000);
    }

    private setIsAccumulated(seriesIds: string[]): void {
        if (this.isAccumulated) return;
        const series = this.serieRepository.getByIds(seriesIds);
        this.isAccumulated = series.some(
            (serie) => serie.props.metadata?.accumulated
        );
    }

    public onAnalyzeWithIA(): void {
        if (!this.accountingPlanService.features?.["IAContador"]) {
            this.requestNandaIA();
            return;
        }
        this.chatNandaService.open();
        setTimeout(() => {
            this.chatNandaService.analyzeVisualization(this.mainVisualization);
        }, 1000);
    }

    requestNandaIA() {
        this.dialog.open(RequestProductGrowthComponent, {
            width: "386px",
            maxWidth: "386px",
            data: {
                title: "Nanda IA",
                imageSrc: "images/nanda-ia-banner.png",
                subtitle: "Nanda IA integrada!",
                description:
                    "O Chat da Nanda IA permite conversar diretamente no <span class='font-weight-semi-bold'>Dashboard, Dash e Showtime</span>, gerando insights e esclarecendo dúvidas de forma rápida.",
                hint: "Essa funcionalidade <span class='font-weight-semi-bold'>não está disponível</span> no seu plano atual. Solicite o contato abaixo para incluir a Nanda IA no seu plano.",
                cta: "Solicitar Nanda IA",
                company: this.company,
                package: "Atalhos da IA do Contador",
                licenses: 1,
                options: {
                    addedValue: "0",
                    period: ""
                }
            }
        });
    }
}
